import { Component, useEffect, useState } from 'react';
import { ButtonToolbar, Col, Row } from 'react-bootstrap';
import moment from 'moment';

import { inject, observer } from 'mobx-react';
import { useComponentWillUnmount } from 'modules/core/hooks';

import Button from '../../../core/components/buttons/Button';
import { DataTable } from '../../../core/components/datatable';
import VehiclePoliciesModal from './VehiclePoliciesModal';
import Can from '../../../permissions/abilities/Can';
import { vehicleActions, VehicleStore } from '../../../vehicles/store/vehicleStore';
import { Policy } from '../../api/policyApi';

export interface PolicyColumnElement {
  name: string,
  title: string,
  component?: Component,
  refId?: number,
  placeholder?: string,
   
  getCellValue?: (row: any, cell: any) => any,
  showTime?: boolean,
  displayFormat?: string,
  visible?: boolean,
}

export interface VehiclePoliciesProps {
  vehicleStore?: VehicleStore,
  vehicleId?: string,
  onlyShowInsurance?: boolean,
}

const VehiclePolicies = (props: VehiclePoliciesProps) => {
  const {
    vehicleStore,
    vehicleId,
    onlyShowInsurance,
  } = props;

  const { policies } = vehicleStore;

  // console.log(policies);

  const [showInsuranceModal, setShowInsuranceModal] = useState<boolean>(false);
  const [currentPolicy, setCurrentPolicy] = useState<Policy>(null);

  const getActive = (value: Policy) => {
    const startDate = moment(value.startDate, 'X');
    const expirationDate = moment(value.expirationDate, 'X');
    const active = (moment().diff(startDate, 'days') > -1)
      && (moment().diff(expirationDate, 'days') < 1);

    return (active ? 'действующий' : 'не действующий');
  };

  const handleDeletePolicy = (policy: Policy) => {
    setShowInsuranceModal(false);
    const idx = policies.findIndex((p) => p.id === policy.id);
    policies[idx] = { ...policy, deleted: true };
    vehicleActions.setPolicies([...policies]);
  };

  const handlePolicyEdit = (policy: Policy) => {
    setCurrentPolicy(policy);
    setShowInsuranceModal(true);
  };

  const handleModalClose = () => {
    setShowInsuranceModal(false);
  };

  const handlePolicyCreate = (policy: Policy) => {
    setShowInsuranceModal(false);
    vehicleActions.setPolicies([{ ...policy, created: true }, ...policies]);
  };

  const handlePolicyChange = (policy: Policy) => {
    setShowInsuranceModal(false);
    const idx = policies.findIndex((p) => p.id === policy.id);
    policies[idx] = { ...policy, updated: true };
    vehicleActions.setPolicies([...policies]);
  };

  useEffect(() => {
    vehicleActions.getPolicies(vehicleId);
  }, [vehicleId]);

  const columns: PolicyColumnElement[] = [
    {
      name: 'insuranceTypeRefValueName',
      title: 'Тип полиса',
    },
    {
      name: 'insuranceCompanyName',
      title: 'Страховая компания',
    },
    {
      name: 'insuranceNumber',
      title: 'Номер полиса',
    },
    {
      name: 'startDate',
      title: 'Дата начала действия',
      getCellValue: (row, cell) => (row[cell] ? moment.unix(row[cell]).format('DD.MM.YYYY') : ''),
    },
    {
      name: 'expirationDate',
      title: 'Дата окончания действия',
      getCellValue: (row, cell) => (row[cell] ? moment.unix(row[cell]).format('DD.MM.YYYY') : ''),
    },
    {
      name: 'active',
      title: 'Активность',
    },
    {
      name: 'editButton',
      title: 'Редактирование',
      visible: !onlyShowInsurance,
      getCellValue: (row) => (
        <Button
          icon='pencil'
          onClick={ () => handlePolicyEdit(row) }
        />
      ),
    }, {
      name: 'deleteButton',
      title: 'Удаление',
      visible: !onlyShowInsurance,
      getCellValue: (row) => (
        <Button
          icon='trash'
          onClick={ () => handleDeletePolicy(row) }
        />
      ),
    },
  ];

  useComponentWillUnmount(() => {
    vehicleActions.setPolicies([]);
  });

  useEffect(() => {
    if (!showInsuranceModal) {
      setCurrentPolicy(null);
    }
  }, [showInsuranceModal]);

  return (
    <>
      <Can on='order.insurancePolicies' do='available' passThrough>
        { (can, permissions) => (
          <>
            { !onlyShowInsurance && <Row>
              <Col md={ 12 }>
                <ButtonToolbar className='float-end'>
                  <Button
                    text='Добавить полис'
                    icon='plus'
                    onClick={ () => setShowInsuranceModal(true) }
                    variant='primary'
                    disabled={ !permissions?.permissionInsert }
                  />
                </ButtonToolbar>
              </Col>
            </Row> }
            <Row>
              <Col md={ 12 }>
                <DataTable
                  columns={ columns }
                  data={ (policies)
                    ? policies.map((v) => { return { ...v, active: getActive(v) }; })
                    : [] }
                />
              </Col>
            </Row>
          </>
        ) }
      </Can>
      { showInsuranceModal
      && (
        <VehiclePoliciesModal
          show={ showInsuranceModal }
          policy={ currentPolicy }
          onCreate={ handlePolicyCreate }
          onChange={ handlePolicyChange }
          onClose={ handleModalClose }
        />
      ) }
    </>
  );
};

export default inject('vehicleStore')(observer(VehiclePolicies));
